'use client';
import cn from 'classnames';
import Image from 'next/image';

import Button from '@/components/common/Button/Button';
import Link from '@/components/common/Link/Link';
import Text from '@/components/common/Text/Text';

import CloseIcon from 'public/img/icon/close.svg';
import ArrowRightIcon from 'public/img/icon/arrow-right-submit-black.svg';

import './BuilderSiteBanner.style.scss';
import { FC, useState, useEffect } from 'react';

export interface BuilderSiteBannerConfig {
  /** Text content to display in the banner on desktop */
  bannerTextDesktop: string;

  /** Text content to display in the banner on mobile */
  bannerTextMobile: string;

  /** Button text for the call-to-action on desktop */
  buttonTextDesktop?: string;

  /** Button text for the call-to-action on mobile */
  buttonTextMobile?: string;

  /** Target for the button to link to */
  buttonTarget?: string;

  /** Button color */
  buttonColor?: string;

  /** Target for the banner to link to */
  bannerTarget?: string;

  /** Whether the banner can be dismissed */
  dismissable?: boolean;

  /** Whether to open the link in a new tab */
  openInNewTab?: boolean;

  /** Background color for the banner */
  backgroundColor?: string;

  /** Text color for the banner */
  textColor?: string;

  /** Optional CSS class name */
  className?: string;

  enabled: boolean;
}

export interface BuilderSiteBannerCMSContent {
  loggedIn: BuilderSiteBannerConfig;
  loggedOut: BuilderSiteBannerConfig;
}

export interface BuilderSiteBannerProps {
  cmsContent?: BuilderSiteBannerConfig;

  /** Function to call when the banner is dismissed */
  handleDismiss?: () => void;
}

const BuilderSiteBanner: FC<BuilderSiteBannerProps> = ({
  cmsContent,
  handleDismiss
}) => {
  const {
    bannerTextDesktop,
    bannerTextMobile,
    buttonTextDesktop,
    buttonTarget = '',
    bannerTarget = '',
    buttonColor = '#121212',
    dismissable = false,
    openInNewTab = false,
    backgroundColor = '#FFC0CB', // Default pink color
    textColor = '#121212', // Default dark text
    className = '',
    enabled
  } = cmsContent || {};

  const [isMobile, setIsMobile] = useState(false);

  // Use effect to see whether window is mobile or desktop style in order to render different components and text
  useEffect(() => {
    // Check if window is available (client-side)
    if (typeof window !== 'undefined') {
      // Set initial state
      setIsMobile(window.innerWidth < 768);

      // Define the resize handler
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };

      // Add event listener
      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  // Check to see if the component is disabled
  if (!enabled) {
    return null;
  }

  // If we have no text content for the current screen size, render nothing. Should only happen when no builder is published
  if (isMobile ? !bannerTextMobile : !bannerTextDesktop && !buttonTextDesktop) {
    return null;
  }

  const bannerText = isMobile ? bannerTextMobile : bannerTextDesktop;
  const buttonText = buttonTextDesktop;

  return (
    // This builder component does not actually get rendered from RenderBuilderContent
    <div
      className={cn('BuilderSiteBanner', className)}
      style={{ backgroundColor, color: textColor }}
      onClick={() => {
        if (bannerTarget) window.open(bannerTarget);
      }}
    >
      {dismissable && (
        <Button
          className='BuilderSiteBanner-close'
          onClick={(e) => {
            e.stopPropagation(); // Stop event from propagating to parent
            if (handleDismiss) handleDismiss();
          }}
          variant='icon'
        >
          <Image
            alt='Close App banner icon'
            src={CloseIcon}
            height={20}
            width={20}
          />
        </Button>
      )}

      {/* Mobile text layout */}
      {isMobile ? (
        <span className='BuilderSiteBanner-mobile'>
          {
            <Link
              className='BuilderSiteBanner-arrow'
              href={buttonTarget}
              target={openInNewTab ? '_blank' : '_self'}
            >
              <Text
                className='BuilderSiteBanner-mobile-text'
                style={{ color: textColor }}
                variant='title-xs'
              >
                {bannerText}{' '}
                <span className='BuilderSiteBanner-mobile-button-text'>
                  {buttonText}
                </span>
                {buttonTarget && (
                  <Image
                    className='BuilderSiteBanner-mobile-arrow-icon'
                    alt='Arrow icon'
                    src={ArrowRightIcon}
                    height={17}
                    width={17}
                  />
                )}
              </Text>
            </Link>
          }
        </span>
      ) : (
        <>
          <Text
            style={{ color: textColor }}
            variant='title-xs'
          >
            {bannerText}
          </Text>

          {/* Desktop button */}
          {buttonText && buttonTarget && (
            <Button
              className='Button small BuilderSiteBannerButton'
              onClick={(e) => {
                e.stopPropagation();
                if (openInNewTab) {
                  window.open(buttonTarget, '_blank');
                } else {
                  window.open(buttonTarget, '_self');
                }
              }}
              style={{
                borderColor: buttonColor,
                color: buttonColor
              }}
            >
              <Text
                style={{ color: buttonColor }}
                variant='title-xxs-500'
              >
                {buttonText}
              </Text>
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default BuilderSiteBanner;
